import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HardDrive, Lightbulb, Search, Sparkles, TicketPlus } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { useUserApps } from '@/hooks/useUserApps'
import { useWindows } from '@/hooks/useWindows'
import { useDebounce } from '@/hooks/useDebounce'
import { cdbTqsApiService } from '@/services/cdb-tqs-api-service'
import type { AppId } from '@/data/apps'
import { fileIconFor } from '@/lib/utils/file-icon'
import { openFile } from '@/lib/files/openFile'
import { OPEN_BUG_REPORT_EVENT, OPEN_FEATURE_REQUEST_EVENT } from '@/services/bug-report-service'
import { useAllowedTicketTypes } from '@/hooks/useAllowedTicketTypes'

type ResultGroup = 'app' | 'document' | 'personalDocument'

interface PaletteResult {
  key: string
  group: ResultGroup
  title: string
  subtitle?: string
  Icon: LucideIcon
  open: () => void
}

const GROUP_ORDER: ResultGroup[] = ['app', 'document', 'personalDocument']
const MAX_APP_RESULTS = 8

// Utility row under the search bar — same 'open-*' CustomEvent convention
// already used by the taskbar-triggered changelog. Both ticket entries open
// the one ticket panel (#1170), preset to their type. `requires` hides an entry
// when the user's roles switched the ticket type off (#1544): "Ticket erstellen"
// stays while any type is left, the panel then falls back to that type.
const UTILITY_ACTIONS: {
  key: string
  event: string
  Icon: LucideIcon
  labelKey: string
  requires?: 'feature' | 'any'
}[] = [
  { key: 'feature-request', event: OPEN_FEATURE_REQUEST_EVENT, Icon: Lightbulb, labelKey: 'ticketPanel.entry.feature', requires: 'feature' },
  { key: 'bug-report', event: OPEN_BUG_REPORT_EVENT, Icon: TicketPlus, labelKey: 'ticketPanel.title', requires: 'any' },
  { key: 'changelog', event: 'open-changelog', Icon: Sparkles, labelKey: 'changelog.title' },
]

/**
 * Global Cmd/Ctrl+K quick-access overlay (cdb_webos#204, step 4).
 * Promoted from the former optional SearchWidget — same permission-filtered
 * useUserApps() source (incl. admin-defined URL apps), plus
 * documents and chats, now reachable from anywhere via a hotkey instead of
 * only when the widget happened to be placed on the desktop.
 */
export function CommandPalette() {
  const { t } = useTranslation()
  const { userApps } = useUserApps()
  const { openWindow } = useWindows()
  const ticketTypes = useAllowedTicketTypes()

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PaletteResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const debouncedQuery = useDebounce(query, 300)

  const closePalette = useCallback(() => {
    setOpen(false)
    setQuery('')
    setResults([])
    setActiveIndex(0)
  }, [])

  // Global hotkey — works anywhere in the desktop shell, not tied to a window.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Taskbar affordance dispatches this — same convention as 'open-changelog'.
  useEffect(() => {
    const handleOpenEvent = () => setOpen(true)
    window.addEventListener('open-command-palette', handleOpenEvent)
    return () => window.removeEventListener('open-command-palette', handleOpenEvent)
  }, [])

  useEffect(() => {
    let cancelled = false

    const search = async () => {
      if (!debouncedQuery.trim()) {
        setResults([])
        return
      }

      setIsLoading(true)
      const lowerQuery = debouncedQuery.toLowerCase()
      const found: PaletteResult[] = []

      // 1. Apps — permission-filtered, includes admin-defined URL apps.
      userApps
        .filter((app) => t(`apps.${app.id}`).toLowerCase().includes(lowerQuery))
        .slice(0, MAX_APP_RESULTS)
        .forEach((app) => {
          found.push({
            key: `app-${app.id}`,
            group: 'app',
            title: t(`apps.${app.id}`),
            Icon: app.Icon ?? Search,
            open: () => openWindow(app.id, t(`apps.${app.id}`)),
          })
        })

      // 2-3. Corporate and personal documents (both server-side search,
      // cdb_tqs#367 follow-up) — independent, run concurrently.
      const [docsResult, personalDocsResult] = await Promise.allSettled([
        cdbTqsApiService.getDocuments(undefined, 5, 0, undefined, debouncedQuery),
        cdbTqsApiService.listPersonalFiles({ search: debouncedQuery, limit: 5 }),
      ])

      if (docsResult.status === 'fulfilled') {
        docsResult.value.documents.forEach((doc) => {
          found.push({
            key: `document-${doc.document_id}`,
            group: 'document',
            title: doc.title || doc.filename,
            subtitle: doc.knowledge_area,
            Icon: fileIconFor(doc.filename),
            open: () =>
              openFile(
                { kind: 'knowledge-area', documentId: doc.document_id, knowledgeArea: doc.knowledge_area, fileName: doc.filename },
                openWindow,
              ),
          })
        })
      }

      if (personalDocsResult.status === 'fulfilled') {
        personalDocsResult.value.items.forEach((file) => {
          found.push({
            key: `personal-document-${file.id}`,
            group: 'personalDocument',
            title: file.filename,
            subtitle: file.description ?? undefined,
            Icon: HardDrive,
            open: () => openWindow('drive2' as AppId, t('apps.drive2')),
          })
        })
      }

      // A newer keystroke already started a fresher search — don't let this
      // stale response clobber it (avoidable if network responses arrive out of order).
      if (cancelled) return

      setResults(found)
      setActiveIndex(0)
      setIsLoading(false)
    }

    search()

    return () => {
      cancelled = true
    }
  }, [debouncedQuery, userApps, t, openWindow])

  const grouped = useMemo(() => {
    return GROUP_ORDER.map((group) => ({
      group,
      items: results.filter((r) => r.group === group),
    })).filter((g) => g.items.length > 0)
  }, [results])

  const flatResults = useMemo(() => grouped.flatMap((g) => g.items), [grouped])

  const handleOpen = (result: PaletteResult) => {
    result.open()
    closePalette()
  }

  const handleUtilityAction = (event: string) => {
    window.dispatchEvent(new CustomEvent(event))
    closePalette()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, flatResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const target = flatResults[activeIndex]
      if (target) handleOpen(target)
    }
  }

  const groupLabel = (group: ResultGroup) => t(`commandPalette.groups.${group}`)

  // Only show a results panel once there's something to show — idle state
  // is just the floating bar and the utility row, nothing below them. Gated
  // on debouncedQuery (not the raw query) so the panel doesn't flash "no
  // results" on every keystroke during the debounce window, before the
  // search effect (which also keys off debouncedQuery) has even run.
  const showResultsPanel = isLoading || grouped.length > 0 || debouncedQuery.trim().length > 0

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) closePalette()
        else setOpen(true)
      }}
    >
      <DialogContent
        hideClose
        hideOverlay
        noAnimation
        className="left-[50%] top-[14vh] translate-x-[-50%] translate-y-0 w-[min(600px,92vw)] max-w-none border-none bg-transparent p-0 gap-0 shadow-none outline-none backdrop-blur-none"
        onOpenAutoFocus={(e: Event) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <DialogTitle className="sr-only">{t('commandPalette.title')}</DialogTitle>

        {/* Floating Spotlight bar */}
        <div className="flex items-center gap-2.5">
          <div className="flex-1 min-w-0 flex items-center gap-2.5 h-12 px-4 rounded-full border border-[var(--window-border-color)] bg-[var(--window-background)] shadow-md">
            <Search className="h-4 w-4 shrink-0 text-[var(--text-secondary)]" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('commandPalette.spotlightPlaceholder')}
              autoComplete="off"
              spellCheck={false}
              className="flex-1 bg-transparent border-none outline-none appearance-none shadow-none focus:shadow-none focus:outline-none focus-visible:outline-none text-[15px] text-[var(--text-color)] placeholder:text-[var(--text-secondary)]"
              style={{ WebkitAppearance: 'none', boxShadow: 'none' }}
            />
          </div>

        </div>

        {/* Utility row — Ticket erstellen / Verbesserung vorschlagen / Changelog (moved off the taskbar) */}
        <div className="mt-2.5 flex items-center justify-center gap-2.5">
          {UTILITY_ACTIONS.filter(({ requires }) => !requires || ticketTypes[requires]).map(({ key, event, Icon, labelKey }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleUtilityAction(event)}
              className="h-10 w-10 shrink-0 flex items-center justify-center rounded-full border border-[var(--window-border-color)] bg-[var(--window-background)] shadow-md text-[var(--text-secondary)] hover:text-[var(--text-color)] transition-colors"
              aria-label={t(labelKey)}
              title={t(labelKey)}
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>

        {/* Results panel — only rendered once there's a query or results */}
        {showResultsPanel && (
          <div className="mt-2 rounded-xl border border-[var(--window-border-color)] bg-[var(--window-background)] shadow-md overflow-hidden">
            <div className="max-h-[55vh] overflow-y-auto p-1.5">
              {isLoading ? (
                <div className="p-8 text-center text-sm text-[var(--text-secondary)]">
                  {t('common.loading', { defaultValue: 'Lädt…' })}
                </div>
              ) : grouped.length > 0 ? (
                grouped.map(({ group, items }) => (
                  <div key={group}>
                    <div className="px-2.5 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                      {groupLabel(group)}
                    </div>
                    {items.map((item) => {
                      const index = flatResults.indexOf(item)
                      const isActive = index === activeIndex
                      return (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => handleOpen(item)}
                          onMouseEnter={() => setActiveIndex(index)}
                          className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition-colors"
                          style={isActive ? { background: 'var(--sidebar-hover)' } : undefined}
                        >
                          <item.Icon className="h-4 w-4 shrink-0 text-[var(--text-secondary)]" />
                          <div className="min-w-0 flex-1">
                            <div className="text-[13.5px] font-medium text-[var(--text-color)] truncate">
                              {item.title}
                            </div>
                            {item.subtitle && (
                              <div className="text-xs text-[var(--text-secondary)] truncate">{item.subtitle}</div>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-sm text-[var(--text-secondary)]">
                  {t('common.noResults', { defaultValue: 'Keine Ergebnisse' })}
                </div>
              )}
            </div>

            <div className="flex items-center gap-4 px-4 py-2 border-t border-[var(--window-border-color)] bg-[var(--window-titlebar-bg)]">
              <span className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)]">
                <kbd className="px-1.5 py-0.5 rounded border border-[var(--window-border-color)] text-[10.5px]">
                  &#8593;&#8595;
                </kbd>
                {t('commandPalette.hintNavigate')}
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)]">
                <kbd className="px-1.5 py-0.5 rounded border border-[var(--window-border-color)] text-[10.5px]">
                  &#8629;
                </kbd>
                {t('commandPalette.hintOpen')}
              </span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
